// Value display contract (plan 8.5.2 · 9.1.6, mockup ui.value / ui.valueBadge / ui.sessionValue / ui.missing):
// every number or cell is exactly one of
//   ① value          — number + unit                                  <Value value={12480} unit="tokens" />
//   ② 0 (confirmed)  — received and really zero                       <Value state="zero" unit="count" />
//   ③ no record      — icon + "기록 없음 · <reason>" badge, 13 reasons  <Value state="none" reason="tool_not_sent" tool="codex" item="active_time" />
//   ④ partial        — value + "일부만 · <reason>" badge                 <Value state="partial" value={1200} partialReason="truncated" />
// Never draw a missing value as 0 or "—". Badges are icon + text (not colour alone) and link to where the reason is fixed.
import type { MissingReason } from '@aiax-hub/contracts';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useCan } from '../auth/AuthContext';
import { useFmt, useHasT, useLang, useT } from '../i18n';
import type { Fmt, Lang, TFn } from '../lib/format';
import { cls } from '../lib/util';
import { Icon } from './Icon';

/** "일부만" reasons (plan 8.5.4). PROVISIONAL here until packages/contracts exports them. */
export const PARTIAL_REASONS = ['truncated', 'estimated_link', 'estimated', 'unofficial', 'verifying'] as const;
export type PartialReason = (typeof PARTIAL_REASONS)[number];

export type ValueState = 'value' | 'zero' | 'none' | 'partial';
export type ValueUnit = 'tokens' | 'count' | 'people' | 'usd' | 'ms' | 'pct' | 'lines' | 'files' | (string & {});

export interface ValueProps {
  state?: ValueState;
  value?: number | string | null;
  unit?: ValueUnit;
  /** Ready-made text instead of the unit formatting. */
  display?: ReactNode;
  /** Full value for the tooltip/aria-label (default: the exact number when it was shortened). */
  title?: string;
  format?: (v: number) => string;
  reason?: MissingReason;
  partialReason?: PartialReason;
  /** Tool-specific explanation key (reasonDetail.<detail>). */
  detail?: string | null;
  /** Support table cell for tool/version reasons: /connect?tab=support&cell=support-<tool>-<item>. */
  tool?: string | null;
  item?: string | null;
  /** false: badge without a link. */
  link?: boolean;
  href?: string;
  /** Short badge for dense table cells (full reason in the name/tooltip). */
  compact?: boolean;
  focusable?: boolean;
}

/** Where a "no record" badge leads (plan 8.5.3 "배지를 누르면"). */
const REASON_HREF: Record<MissingReason, string | 'support' | null> = {
  consent_before: '/me?tab=level',
  paused: '/me?tab=level',
  level_off: '/me?tab=level',
  cli_not_sent: '/connect?tab=mine',
  tool_not_sent: 'support',
  version_checking: 'support',
  old_version: 'support',
  verifying: 'support',
  expired: '/glossary/retention',
  gap: '/connect?tab=mine',
  small_group: '/glossary/small-group-protection',
  not_applicable: null,
  permission: null,
};
export const supportCellId = (tool: string, item: string): string => `support-${tool}-${item}`;

export function reasonHref(reason: MissingReason, tool?: string | null, item?: string | null, canSystemView = false): string | null {
  const href = REASON_HREF[reason];
  if (href === 'support') return '/connect?tab=support' + (tool && item ? '&cell=' + supportCellId(tool, item) : '');
  if (reason === 'gap' && canSystemView) return '/admin/collection';
  return href ?? null;
}

/** Text of a value in a unit (mockup valueText). */
export function valueText(v: number | string | null | undefined, o: Pick<ValueProps, 'unit' | 'format'>, fmt: Fmt, lang: Lang): string {
  if (v == null) return '';
  if (typeof v !== 'number') return String(v);
  if (o.format) return o.format(v);
  const en = lang === 'en';
  switch (o.unit) {
    case 'tokens':
      return fmt.tok(v);
    case 'usd':
      return fmt.usd(v);
    case 'ms':
      return fmt.dur(v);
    case 'pct':
      return fmt.pct(v, 1);
    case 'count':
      return fmt.compact(v) + (en ? '' : '건');
    case 'people':
      return fmt.int(v) + (en ? '' : '명');
    case 'lines':
      return fmt.compact(v) + (en ? ' lines' : '줄');
    case 'files':
      return fmt.int(v) + (en ? '' : '개');
    default:
      return o.unit ? `${fmt.compact(v)} ${o.unit}` : fmt.compact(v);
  }
}
function fullValueText(v: number, unit: ValueUnit | undefined, fmt: Fmt, lang: Lang): string {
  const en = lang === 'en';
  return fmt.int(v) + (unit === 'tokens' ? (en ? ' tokens' : '토큰') : unit === 'count' ? (en ? '' : '건') : '');
}

/** One-line text of a value state (aria labels, CSV, sentences) — App.valueText. */
export function valueStateText(o: ValueProps, t: TFn, fmt: Fmt, lang: Lang): string {
  const st = o.state ?? (o.value == null ? 'none' : 'value');
  if (st === 'none') return `${t('value.none')} · ${t('reason.' + (o.reason ?? 'permission'))}`;
  const v = valueText(st === 'zero' ? 0 : o.value, o, fmt, lang);
  return st === 'partial' ? `${v} (${t('value.partial')} · ${t('partial.' + (o.partialReason ?? o.reason ?? 'estimated'))})` : v;
}

function BadgeLink({ href, className, title, label, focusable, children }: { href: string | null; className: string; title: string; label: string; focusable?: boolean; children: ReactNode }) {
  return href ? (
    <Link className={className} to={href} title={title} aria-label={label}>
      {children}
    </Link>
  ) : (
    <span className={className} title={title} aria-label={label} tabIndex={focusable ? 0 : undefined}>
      {children}
    </span>
  );
}

function NoneBadge(o: ValueProps) {
  const t = useT();
  const hasT = useHasT();
  const { can } = useCan();
  const reason = o.reason ?? 'permission';
  const label = t('reason.' + reason);
  const help = (o.detail && hasT('reasonDetail.' + o.detail) ? t('reasonDetail.' + o.detail) + ' · ' : '') + t(`reason.${reason}.help`);
  const href = o.link === false ? null : (o.href ?? reasonHref(reason, o.tool, o.item, can('system.view')));
  const tip = `${t('value.none')} · ${label} — ${help}`;
  const ic = <Icon name={reason === 'permission' ? 'lock' : reason === 'small_group' ? 'users' : 'ban'} className="val-badge__ic" />;
  return (
    <BadgeLink href={href} className={cls('val-badge val-badge--none', o.compact && 'val-badge--compact')} title={o.compact ? tip : help} label={tip} focusable={o.focusable}>
      {ic}
      {o.compact ? (
        <>
          <span className="visually-hidden">{`${t('value.none')} · ${label}`}</span>
          <span className="val-badge__r" aria-hidden="true">
            {hasT('reasonShort.' + reason) ? t('reasonShort.' + reason) : label}
          </span>
        </>
      ) : (
        <>
          <span className="val-badge__k">{t('value.none')}</span>
          <span className="val-badge__r">{' · ' + label}</span>
        </>
      )}
    </BadgeLink>
  );
}

function PartialBadge(o: ValueProps) {
  const t = useT();
  const hasT = useHasT();
  const { can } = useCan();
  const pr = o.partialReason ?? (o.reason as PartialReason | undefined) ?? 'estimated';
  const help = (o.detail && hasT('reasonDetail.' + o.detail) ? t('reasonDetail.' + o.detail) + ' · ' : '') + t(`partial.${pr}.help`);
  const href = o.link === false ? null : (o.href ?? (pr === 'verifying' ? reasonHref('verifying', o.tool, o.item, can('system.view')) : null));
  const aria = `${t('value.partial')} · ${t('partial.' + pr)} — ${help}`;
  return (
    <BadgeLink href={href} className={cls('val-badge val-badge--partial', o.compact && 'val-badge--compact')} title={o.compact ? aria : help} label={aria} focusable={o.focusable}>
      <Icon name="info" className="val-badge__ic" />
      {o.compact ? (
        <>
          <span className="visually-hidden">{`${t('value.partial')} · ${t('partial.' + pr)}`}</span>
          <span className="val-badge__r" aria-hidden="true">
            {hasT('partialShort.' + pr) ? t('partialShort.' + pr) : t('partial.' + pr)}
          </span>
        </>
      ) : (
        <>
          <span className="val-badge__k">{t('value.partial')}</span>
          <span className="val-badge__r">{' · ' + t('partial.' + pr)}</span>
        </>
      )}
    </BadgeLink>
  );
}

/** A value in one of the four contract states. */
export function Value(o: ValueProps) {
  const t = useT();
  const fmt = useFmt();
  const lang = useLang();
  const st: ValueState = o.state ?? (o.value == null ? 'none' : o.value === 0 ? 'zero' : 'value');
  if (st === 'none')
    return (
      <span className="val val--none" data-reason={o.reason ?? 'permission'}>
        <NoneBadge {...o} />
      </span>
    );
  const num = st === 'zero' ? 0 : o.value;
  const disp = o.display ?? valueText(num, o, fmt, lang);
  const full = o.title ?? (typeof num === 'number' && Math.abs(num) >= 10000 ? fullValueText(num, o.unit, fmt, lang) : undefined);
  const name = full ?? (st === 'zero' ? t('value.zero') : undefined);
  const valEl = (
    <span className="val__num" title={name} aria-label={name}>
      {disp}
    </span>
  );
  if (st === 'partial')
    return (
      <span className="val val--partial" data-reason={o.partialReason ?? o.reason ?? 'estimated'}>
        {valEl} <PartialBadge {...o} />
      </span>
    );
  return <span className={cls('val', st === 'zero' && 'val--zero')}>{valEl}</span>;
}

/** Badge only, without the number (KPI second line, table cell tail) — ui.valueBadge. */
export function ValueBadge(o: ValueProps) {
  return (o.state ?? 'none') === 'partial' ? <PartialBadge {...o} /> : <NoneBadge {...o} />;
}

/** Per-field state as the API sends it for one record (value / partial / none + reason). */
export interface FieldState {
  state: 'value' | 'partial' | 'none';
  reason?: MissingReason | PartialReason | null;
  detail?: string | null;
  tool?: string | null;
  item?: string | null;
}
/** One record cell by the value contract — ui.sessionValue(session, field, { value, unit }). */
export function SessionValue({ field, ...o }: { field: FieldState } & Omit<ValueProps, 'state' | 'reason' | 'partialReason'>) {
  const state: ValueState = field.state === 'value' ? (o.value === 0 ? 'zero' : 'value') : field.state;
  return (
    <Value
      {...o}
      state={state}
      reason={field.state === 'none' ? ((field.reason as MissingReason | null) ?? undefined) : undefined}
      partialReason={field.state === 'partial' ? ((field.reason as PartialReason | null) ?? undefined) : undefined}
      detail={field.detail}
      tool={field.tool}
      item={field.item}
    />
  );
}

/** v0 reason names kept for ported screen code → [v1 reason, tool-specific detail] (mockup LEGACY_REASON). */
export const LEGACY_REASON: Readonly<Record<string, readonly [MissingReason, string?]>> = {
  consent: ['consent_before'],
  level: ['level_off'],
  not_sent: ['cli_not_sent'],
  na: ['not_applicable'],
  perm: ['permission'],
  small_group: ['small_group'],
  codex_active: ['tool_not_sent', 'codex_active'],
  codex_loc: ['tool_not_sent', 'codex_loc'],
  api_time: ['not_applicable', 'api_time'],
  api_files: ['not_applicable', 'api_files'],
  claude_reasoning: ['tool_not_sent', 'claude_reasoning'],
  unofficial: ['tool_not_sent', 'codex_cost'],
  not_parsed: ['version_checking'],
};

/** "기록 없음 · 이유" for a reason key (v1 or v0) — ui.missing. */
export function Missing({ reason, ...o }: { reason: string } & Omit<ValueProps, 'state' | 'reason'>) {
  const t = useT();
  const hasT = useHasT();
  const v1 = (REASON_HREF as Record<string, unknown>)[reason] !== undefined ? ([reason as MissingReason] as const) : LEGACY_REASON[reason];
  if (v1) return <Value {...o} state="none" reason={v1[0]} detail={v1[1] ?? o.detail ?? null} />;
  const r = reason && hasT('missing.' + reason) ? t('missing.' + reason) : reason;
  return (
    <span className="missing">
      <span className="missing__label">{t('missing.label')}</span>
      {r ? <span className="missing__reason">{' · ' + r}</span> : null}
    </span>
  );
}
